import { AuthStrategy, AuthResult, UserInfo } from "../types";
import { storeAuth, getStoredToken, getStoredUser, clearStoredAuth } from "../token-storage";
import { openOfficeDialog } from "../../utils/office-dialog";
import { authConfig } from "../../config/auth.config";

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (let i = 0; i < bytes.length; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const verifier = base64UrlEncode(array.buffer);

  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const challenge = base64UrlEncode(digest);

  return { verifier, challenge };
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Invalid JWT");
  const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(payload));
}

export class Auth0Strategy implements AuthStrategy {
  isAuthenticated(): boolean {
    return getStoredToken() !== null;
  }

  async login(): Promise<AuthResult> {
    const { verifier, challenge } = await generatePKCE();

    const callbackUrl = `${authConfig.appUrl}/dialog/auth-callback.html`;

    const authorizeParams = new URLSearchParams({
      response_type: "code",
      client_id: authConfig.clientId,
      redirect_uri: callbackUrl,
      scope: "openid profile email",
      code_challenge: challenge,
      code_challenge_method: "S256",
      ...(authConfig.audience ? { audience: authConfig.audience } : {}),
    });

    const authUrl = `https://${authConfig.domain}/authorize?${authorizeParams}`;

    // Build the dialog start URL. We pass PKCE params via the URL so the
    // callback page (which runs in the dialog's browser instance) can use them.
    // The dialog shares sessionStorage within its own origin, so we write
    // the verifier there via the start page.
    const startUrl = new URL(`${authConfig.appUrl}/dialog/auth-start.html`);
    startUrl.searchParams.set("authUrl", authUrl);
    startUrl.searchParams.set("codeVerifier", verifier);
    startUrl.searchParams.set("auth0Domain", authConfig.domain);
    startUrl.searchParams.set("auth0ClientId", authConfig.clientId);

    const messageStr = await openOfficeDialog(startUrl.toString(), {
      height: 60,
      width: 30,
    });

    const message = JSON.parse(messageStr);

    if (message.status === "error") {
      throw new Error(message.errorDescription || message.error);
    }

    const claims = message.idToken
      ? decodeJwtPayload(message.idToken)
      : ({} as Record<string, unknown>);

    const user: UserInfo = {
      sub: (claims.sub as string) || "",
      email: claims.email as string | undefined,
      name: claims.name as string | undefined,
      picture: claims.picture as string | undefined,
    };

    const expiresAt = Date.now() + (message.expiresIn || 3600) * 1000;

    const result: AuthResult = {
      accessToken: message.accessToken,
      idToken: message.idToken,
      expiresAt,
      user,
    };

    storeAuth(result);
    return result;
  }

  async logout(): Promise<void> {
    clearStoredAuth();
  }

  async getToken(): Promise<string | null> {
    return getStoredToken();
  }

  async getUser(): Promise<UserInfo | null> {
    if (!this.isAuthenticated()) return null;
    return getStoredUser();
  }
}
